import React from 'react';
import { Activity, CheckSquare, Clock, PhoneCall, Mail, MessageSquare, FileText, Receipt, Wallet, FileMinus, BellRing, FileSignature } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';

const ACTIVITY_ICONS = {
  call: PhoneCall,
  email: Mail,
  whatsapp_message: MessageSquare,
  instagram_message: MessageSquare,
  instagram_comment: MessageSquare,
  note: FileText,
  meeting: Clock,
  status_change: Activity,
  stage_change: Activity,
  invoice: Receipt,
  payment: Wallet,
  quotation: FileSignature,
  credit_note: FileMinus,
  payment_reminder: BellRing,
};

/**
 * Merges CrmActivity entries and open/completed Tasks into a single
 * chronological feed — replaces the previous "History" tab, which only
 * rendered the raw AuditLogService diff dump with no interleaving of the
 * calls/emails/notes/status-changes/tasks that actually tell the story of
 * a record's lifecycle.
 */
export default function UnifiedTimeline({ activities = [], tasks = [] }) {
  const entries = [
    ...activities.map(a => ({
      key: `activity-${a._id}`,
      date: a.createdAt,
      icon: ACTIVITY_ICONS[a.type] || Activity,
      title: a.title,
      description: a.description,
      badge: (a.type || '').replace(/_/g, ' '),
    })),
    ...tasks.map(t => ({
      key: `task-${t._id}`,
      date: t.dueDate || t.createdAt,
      icon: CheckSquare,
      title: t.title,
      description: t.description,
      badge: t.status === 'completed' ? 'task completed' : 'task due',
    })),
  ]
    .filter(e => e.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  if (entries.length === 0) {
    return (
      <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded bg-white opacity-40">
        <Activity size={48} className="mx-auto mb-4 opacity-10" />
        <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No activity recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-in slide-in-from-bottom-4">
      {entries.map(e => {
        const Icon = e.icon;
        return (
          <div key={e.key} className="sf-card p-4 flex gap-4 hover:border-[#0176D3] transition-all bg-white">
            <div className="w-10 h-10 rounded border border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-center shrink-0">
              <Icon size={18} className="text-[#0176D3]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h4 className="text-[14px] font-bold text-[#080707] truncate">{e.title}</h4>
                <span className="text-[11px] font-bold text-[#706E6B] shrink-0">{formatDate(e.date)}</span>
              </div>
              {e.description && <p className="text-[12px] text-[#706E6B] line-clamp-2">{e.description}</p>}
              <span className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">{e.badge}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
